import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ResourceList } from '../components/ResourceList.js';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { SyncStatusBadge, type SyncStatus } from '../components/SyncStatusBadge.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';
import { useAuth } from '../auth/AuthContext.js';
import { Button, Icon, Spinner } from '@ng-unifi/ui';

interface AccessGroupRow {
  id: string;
  tenant_id?: string;
  name: string;
  schedule_id: string | null;
  schedule?: ScheduleSummary | null;
  sync_status: SyncStatus;
  last_sync_error: string | null;
  created_at: string;
  _count?: { members: number; resources: number };
}

interface ScheduleSummary {
  id: string;
  name: string;
}

interface GrantedResourceRow {
  building_resource_id: string;
  label: string;
  unifi_resource_type: 'door' | 'door_group' | 'device';
  unifi_resource_id: string;
  source_zones?: Array<{ id: string; name: string }>;
  direct_grant?: boolean;
}

async function loadGroups(scopeOpts?: ApiRequestInit): Promise<AccessGroupRow[]> {
  const res = await apiClient.get<{ data: AccessGroupRow[] }>('/api/v1/access-groups', scopeOpts);
  return res.data;
}

export function AccessGroupsPage() {
  const { state } = useAuth();
  const { scopeOpts, effectiveTenantId, isMasterWithoutContext, tenantName, scopedDetailHref } =
    useTenantScopedPage();
  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';
  const [createOpen, setCreateOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  // UX-014: ScheduleDetailPage links here with ?schedule=<id> to answer "which
  // zones use this schedule". Nothing read it, so the operator got the whole
  // list. The rows already carry schedule_id, so filter client-side rather than
  // asking the backend for a parameter it does not have.
  const [searchParams] = useSearchParams();
  const scheduleFilter = searchParams.get('schedule')?.trim() ?? '';
  const load = useCallback(
    () =>
      effectiveTenantId
        ? loadGroups(scopeOpts).then((groups) =>
            scheduleFilter ? groups.filter((g) => g.schedule_id === scheduleFilter) : groups,
          )
        : Promise.resolve([]),
    [effectiveTenantId, scopeOpts, scheduleFilter],
  );

  if (isMasterWithoutContext) {
    return (
      <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-6">
        <h1 className="font-headline-md text-headline-md text-on-surface">Access Zones</h1>
        <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
          Open a tenant from the Tenants page to manage its access zones.
        </p>
        <Link
          to="/tenants"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-label-md text-label-md text-on-primary"
        >
          <Icon name="apartment" />
          Tenants
        </Link>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {toast ? (
        <div
          role="status"
          className="flex items-center gap-2 rounded-lg bg-secondary-container px-4 py-2 font-body-md text-body-md text-on-secondary-container"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" className="underline" onClick={() => setToast(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {scheduleFilter ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-container px-4 py-3 font-body-md text-body-md text-on-surface-variant"
        >
          <Icon name="filter_alt" />
          <span className="flex-1">Showing only Access Zones on the selected schedule.</span>
          <Link to="/access-groups" className="text-primary underline hover:no-underline">
            Show all
          </Link>
        </div>
      ) : null}
      <ResourceList<AccessGroupRow>
        key={`${reloadTick}:${scheduleFilter}`}
        title="Access Zones"
        subtitle={
          tenantName
            ? `Tenant-managed access zones for ${tenantName}.`
            : 'Tenant-managed access zones use the doors granted by the building, then sync membership and schedules to UniFi.'
        }
        icon="key"
        action={{
          label: 'New Access Zone',
          icon: 'add',
          onClick: () => setCreateOpen(true),
        }}
        load={load}
        emptyMessage="No tenant Access Zones yet."
        columns={[
          {
            key: 'name',
            header: 'Name',
            render: (g) =>
              isMaster ? (
                <Link
                  // In the compact card layout this row link sits within 24px
                  // of the next field's control, so it fails 2.5.8's spacing
                  // exception at 390px and has to meet the size floor itself.
                  className="inline-flex min-h-[1.5rem] items-center text-primary hover:underline"
                  to={scopedDetailHref('/access-groups', g.id, undefined, g.tenant_id) ?? '#'}
                >
                  {g.name}
                </Link>
              ) : (
                <span>{g.name}</span>
              ),
          },
          {
            key: 'schedule',
            header: 'Schedule',
            width: '24%',
            render: (g) => g.schedule?.name ?? 'No schedule assigned',
          },
          {
            key: 'members',
            header: 'People',
            width: '12%',
            render: (g) => String(g._count?.members ?? 0),
          },
          {
            key: 'resources',
            header: 'Doors',
            width: '12%',
            render: (g) => String(g._count?.resources ?? 0),
          },
          {
            key: 'sync_status',
            header: 'Sync',
            width: '15%',
            render: (g) => <SyncStatusBadge status={g.sync_status} />,
          },
          {
            key: 'created_at',
            header: 'Created',
            width: '20%',
            render: (g) => new Date(g.created_at).toLocaleDateString(),
          },
        ]}
      />
      {createOpen ? (
        <CreateAccessZoneModal
          scopeOpts={scopeOpts}
          onCancel={() => setCreateOpen(false)}
          onDone={(message) => {
            setCreateOpen(false);
            setToast(message);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateAccessZoneModal({
  scopeOpts,
  onCancel,
  onDone,
}: {
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [scheduleId, setScheduleId] = useState('');
  const [resources, setResources] = useState<GrantedResourceRow[]>([]);
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      apiClient.get<{ data: ScheduleSummary[] }>('/api/v1/schedules', scopeOpts),
      apiClient.get<{ data: GrantedResourceRow[] }>('/api/v1/hardware/resources', scopeOpts),
    ])
      .then(([scheduleRes, resourceRes]) => {
        if (cancelled) return;
        const usableSchedules = Array.isArray(scheduleRes.data) ? scheduleRes.data : [];
        const usableResources = (Array.isArray(resourceRes.data) ? resourceRes.data : []).filter(
          (resource) =>
            resource.unifi_resource_type === 'door' || resource.unifi_resource_type === 'door_group',
        );
        setSchedules(usableSchedules);
        setResources(usableResources);
        setScheduleId((current) => current || usableSchedules[0]?.id || '');
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load access-zone options.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeOpts]);

  function toggleResource(resourceId: string) {
    setSelectedResourceIds((current) =>
      current.includes(resourceId)
        ? current.filter((id) => id !== resourceId)
        : [...current, resourceId],
    );
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!scheduleId) {
      setError('Choose a schedule before creating an Access Zone.');
      return;
    }
    if (selectedResourceIds.length === 0) {
      setError('Choose at least one granted door or door group.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(
        '/api/v1/access-groups',
        {
          name: name.trim(),
          schedule_id: scheduleId,
          resource_ids: selectedResourceIds,
        },
        scopeOpts,
      );
      onDone('Access Zone created. Controller sync is queued.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create Access Zone.');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-scrim/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-access-zone-title"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-outline-variant bg-surface p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="create-access-zone-title" className="font-title-lg text-title-lg text-on-surface">
              New Access Zone
            </h2>
            <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
              Choose the schedule and granted doors that this tenant can assign to employees.
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-on-surface-variant hover:bg-surface-container"
            onClick={onCancel}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Name" htmlFor="agx-name">
              <input
                id="agx-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={fieldCls}
                maxLength={120}
                required
              />
            </Field>

            <Field label="Schedule" htmlFor="agx-schedule">
              <select
                id="agx-schedule"
                value={scheduleId}
                onChange={(e) => setScheduleId(e.target.value)}
                className={fieldCls}
                disabled={schedules.length === 0}
              >
                <option value="">Select schedule</option>
                {schedules.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </option>
                ))}
              </select>
            </Field>
            {schedules.length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                Create and sync an unlock schedule before building an Access Zone.
              </p>
            ) : null}

            <div className="space-y-2">
              <span className="font-label-md text-label-md text-on-surface-variant">
                Granted doors
              </span>
              {resources.length === 0 ? (
                <p className="rounded border border-outline-variant p-3 font-body-md text-body-md text-on-surface-variant">
                  No granted door resources are available for this tenant yet.
                </p>
              ) : (
                <div className="max-h-72 divide-y divide-outline-variant overflow-y-auto rounded border border-outline-variant">
                  {resources.map((resource) => (
                    <label
                      key={resource.building_resource_id}
                      className="flex items-start gap-3 px-3 py-3"
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedResourceIds.includes(resource.building_resource_id)}
                        onChange={() => toggleResource(resource.building_resource_id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-body-md text-body-md text-on-surface">
                          {resource.label}
                        </span>
                        <span className="block break-words font-body-sm text-body-sm text-on-surface-variant">
                          {resource.unifi_resource_type} · {resource.unifi_resource_id}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error ? (
              <p role="alert" className="font-body-md text-body-md text-error">
                {error}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={submitting}
                disabled={schedules.length === 0 || resources.length === 0}
              >
                Create Access Zone
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const fieldCls =
  'w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface';

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block space-y-1">
      <span className="font-label-md text-label-md text-on-surface-variant">{label}</span>
      {children}
    </label>
  );
}
